import { useId, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { VirtualizedTable } from '@/components/ui/VirtualizedTableBody';
import {
  WORKSPACE_PAGE_SIZE_OPTIONS,
  paginationRangeLabel,
  totalPages,
  type WorkspacePageSize,
} from '@/hooks/useServerPagination';
import { WorkspaceKpiSummarySkeleton } from '@/components/workspace/WorkspaceKpiSummary';
import { workspaceStickyCheckboxCellClass } from '@/components/workspace/workspaceStickyColumns';

const SKELETON_BAR = 'block animate-pulse rounded bg-surface-muted';

export function WorkspaceTableSkeleton({
  rows = 8,
  columns = 8,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <div
      className="max-w-full min-w-0 overflow-hidden rounded-xl border border-border bg-surface shadow-md"
      role="status"
      aria-busy="true"
      aria-label="Loading table"
    >
      <table className={workspaceTableClass.table}>
        <thead>
          <tr>
            {Array.from({ length: columns }, (_, index) => (
              <th key={index} className={workspaceTableClass.th}>
                <span
                  className={`${SKELETON_BAR} h-3 ${index === 0 ? 'w-24' : 'ml-auto w-14'}`}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {Array.from({ length: rows }, (_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }, (_, columnIndex) => (
                <td key={columnIndex} className={workspaceTableClass.td}>
                  <span
                    className={`${SKELETON_BAR} h-4 ${
                      columnIndex === 0 ? 'w-36 max-w-full' : 'ml-auto w-16'
                    }`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const WORKSPACE_PAGE_SKELETON_SECTIONS = [
  'Performance by ad product',
  'Targeting Type Breakdown',
  'Match Type Breakdown',
  'Portfolios',
  'Campaigns',
  'Ad groups',
  'Keywords',
] as const;

export function WorkspacePageSkeleton() {
  return (
    <div className="mx-auto max-w-[1600px] space-y-3.5 px-6 pb-[18px] pt-1 font-sans">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Workspace</h1>
        <p className="mt-0.5 text-body-md text-foreground-muted">
          Select rows in a table, then use that table&apos;s Apply filter button to filter the
          hierarchy.
        </p>
      </header>
      <WorkspaceKpiSummarySkeleton />
      {WORKSPACE_PAGE_SKELETON_SECTIONS.map((title) => (
        <WorkspaceSection key={title} title={title}>
          <WorkspaceTableSkeleton />
        </WorkspaceSection>
      ))}
    </div>
  );
}

export function WorkspaceSection({
  id,
  title,
  countLabel,
  actions,
  toolbar,
  children,
}: {
  id?: string;
  /** Omit when the page header already names the section. */
  title?: string;
  countLabel?: string;
  actions?: ReactNode;
  /** Search and filter controls. Shares the actions row when there is no title. */
  toolbar?: ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="space-y-3">
      <div className={cn('flex flex-wrap gap-2', !title && toolbar ? 'items-end gap-3' : 'items-center')}>
        {!title && toolbar ? <div className="min-w-0 flex-[1_1_28rem]">{toolbar}</div> : null}
        {title ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="font-display text-headline-md text-foreground">{title}</h2>
            {countLabel ? (
              <span className="font-mono text-body-sm text-foreground-subtle">{countLabel}</span>
            ) : null}
          </div>
        ) : null}
        {/* Without a title, the count sits beside the toolbar actions. */}
        {actions || (!title && countLabel) ? (
          <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
            {!title && countLabel ? (
              <span className="font-mono text-body-sm text-foreground-subtle">{countLabel}</span>
            ) : null}
            {actions}
          </div>
        ) : null}
      </div>
      {title && toolbar ? <ToolbarRow>{toolbar}</ToolbarRow> : null}
      {children}
    </section>
  );
}

export function WorkspaceTableShell({
  children,
  emptyMessage,
  isEmpty,
  isLoading = false,
  footer,
}: {
  children: React.ReactNode;
  emptyMessage: string;
  isEmpty: boolean;
  isLoading?: boolean;
  footer?: ReactNode;
}) {
  if (isLoading) {
    return <WorkspaceTableSkeleton />;
  }

  if (isEmpty) {
    return (
      <div className="relative z-0 flex flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-md">
        <div className="px-5 py-[60px] text-center">
          <div className="font-display text-[17px] font-semibold tracking-[-0.01em] text-foreground">
            No rows match these filters
          </div>
          <div className="mt-1 text-body-md text-foreground-subtle">{emptyMessage}</div>
        </div>
        {footer}
      </div>
    );
  }

  return (
    <div className="relative z-0 flex max-w-full min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-md">
      <div className="app-table-scroll custom-scrollbar isolate max-h-[28rem] max-w-full min-w-0 overflow-x-auto overflow-y-auto">
        {children}
      </div>
      {footer}
    </div>
  );
}

export function WorkspaceVirtualizedTableShell<T>({
  rows,
  emptyMessage,
  isEmpty,
  renderRow,
  header,
  estimateRowHeight = 52,
}: {
  rows: T[];
  emptyMessage: string;
  isEmpty: boolean;
  renderRow: (row: T, index: number) => ReactNode;
  header: ReactNode;
  estimateRowHeight?: number;
}) {
  if (isEmpty) {
    return (
      <div className="rounded-xl border border-border bg-surface px-5 py-[60px] text-center shadow-md">
        <div className="font-display text-[17px] font-semibold tracking-[-0.01em] text-foreground">
          No rows match these filters
        </div>
        <div className="mt-1 text-body-md text-foreground-subtle">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="relative z-0 max-w-full min-w-0 overflow-hidden rounded-xl border border-border bg-surface shadow-md">
      <VirtualizedTable
        rows={rows}
        renderRow={renderRow}
        header={header}
        maxHeight="28rem"
        tableClassName={workspaceTableClass.table}
        estimateRowHeight={estimateRowHeight}
      />
    </div>
  );
}

export const workspaceTableClass = {
  table: 'w-max min-w-full border-separate border-spacing-0 text-[12px]',
  th: 'sticky top-0 z-30 whitespace-nowrap bg-surface-header px-3 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-foreground',
  td: 'min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap border-b border-surface-muted px-1 py-2 align-middle',
  rowSelected: 'bg-selection-bar',
  rowHover: 'hover:bg-surface-muted',
};

/**
 * Body inset so categorical values line up with header title text.
 * Header uses `px-3` + `w-4` grip + `gap-2`; body cells use `px-1`.
 */
export const WORKSPACE_COLUMN_TITLE_INSET_CLASS = 'pl-8';

export function WorkspaceMiddleCell({
  children,
  className,
  style,
  alignToTitle = false,
}: {
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  alignToTitle?: boolean;
}) {
  return (
    <td style={style} className={cn(workspaceTableClass.td, 'align-middle text-left leading-none', className)}>
      <div
        className={cn(
          'flex h-full w-full min-w-0 items-center justify-start leading-none',
          alignToTitle && WORKSPACE_COLUMN_TITLE_INSET_CLASS
        )}
      >
        {children}
      </div>
    </td>
  );
}

export function WorkspaceCheckboxCell({
  isHeader = false,
  children,
}: {
  isHeader?: boolean;
  children?: ReactNode;
}) {
  const className = workspaceStickyCheckboxCellClass(isHeader);
  if (isHeader) {
    return (
      <th className={className} style={{ zIndex: 40, top: 0, left: 0 }}>
        {children}
      </th>
    );
  }
  return <td className={className}>{children}</td>;
}

export function SelectionCheckbox({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <span className="inline-flex align-middle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
        disabled={disabled}
        className="m-0 block h-3.5 w-3.5 shrink-0 rounded border-border accent-brand focus:ring-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
      />
    </span>
  );
}

export function CountSubtitle({ visible, total }: { visible: number; total: number }) {
  return (
    <p className="text-[11px] text-foreground-muted">
      Showing <span className="font-mono">{visible}</span> of <span className="font-mono">{total}</span>
    </p>
  );
}

export function WorkspaceTablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  disabled = false,
}: {
  page: number;
  pageSize: WorkspacePageSize;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: WorkspacePageSize) => void;
  disabled?: boolean;
}) {
  const rowsSelectId = useId();
  const pages = totalPages(total, pageSize);
  const canPrev = page > 1 && !disabled;
  const canNext = page < pages && !disabled;

  return (
    <div
      className="flex flex-wrap items-center gap-3 border-t border-border bg-surface-header px-4 py-2"
      aria-busy={disabled}
    >
      <span className="text-body-sm text-foreground-muted">
        {paginationRangeLabel(page, pageSize, total)}
      </span>
      <span className="h-4 w-px bg-border" aria-hidden />
      <div className="flex items-center gap-2">
        <label htmlFor={rowsSelectId} className="text-body-sm text-foreground-muted">
          Rows
        </label>
        <select
          id={rowsSelectId}
          value={pageSize}
          disabled={disabled}
          onChange={(e) => onPageSizeChange(Number(e.target.value) as WorkspacePageSize)}
          className="h-7 rounded-md border border-border bg-surface px-2 font-mono text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:opacity-50"
        >
          {WORKSPACE_PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
      <div className="ml-auto flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 min-w-7 rounded-md px-1.5"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          ‹
        </Button>
        <span className="min-w-[3ch] text-center font-mono text-body-sm text-foreground-muted">
          {page}/{pages || 1}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 min-w-7 rounded-md px-1.5"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          ›
        </Button>
      </div>
    </div>
  );
}

export function ToolbarRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">{children}</div>
  );
}

export function FilterSelect({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-label-md uppercase text-foreground-subtle">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-lg border border-border bg-surface px-3 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
      >
        {children}
      </select>
    </div>
  );
}

export function FilterSearch({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="min-w-[200px] flex-1">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? `Search ${label.toLowerCase()}…`}
        className="h-8 w-full rounded-lg border border-border bg-surface px-3 text-body-sm placeholder:text-foreground-subtle focus:outline-none focus:ring-2 focus:ring-brand/30"
      />
    </div>
  );
}

export function tableRowClass(selected: boolean): string {
  return cn(workspaceTableClass.rowHover, selected && workspaceTableClass.rowSelected);
}
